import React from 'react';
import {
  OperationalModelOptions,
  OperationalStatusOptions,
} from '@components/EntityDrawers/constants/common';
import FilterSelector from '@components/tables/core/FilterSelector';
import { OperationalModel } from '@constants/canvas/layers';
import { useProjectFilters } from '@context/ProjectFilters';
import useChangeFilterHandler from '@hooks/useChangeFilterHandler';
import Filters from '@views/Project/components/Filters/Filters';
import CiaFilters from '@views/Project/components/Toolbox/components/ModelFilters/components/CiaFilters';
import PartnersFilterSelector from '@views/Project/components/Toolbox/components/PartnersFilterSelector';

const ModelFilters = () => {
  const changeFilterHandler = useChangeFilterHandler();

  const {
    validationStatus,
    reviewStatus,
    operationalStatuses,
    operatingModelStatuses,
    validationStatusOptions,
    reviewStatusOptions,
    setReviewStatus,
    setValidationStatus,
    setOperationalStatuses,
    setOperatingModelStatuses,
    partners,
    setPartners,
    selectedFilters,
    handleClearAllFilters,
  } = useProjectFilters();

  const handleSetReview = changeFilterHandler({
    isNodeVisible: (_, node) => node?.data.dto.review_required,
    onSetValues: setReviewStatus,
  });

  const handleSetStatus = changeFilterHandler({
    isNodeVisible: (statuses, node) => {
      return statuses.includes(node?.data.dto.meta_data.valid_status);
    },
    onSetValues: setValidationStatus,
  });

  const handleSetOperationalStatus = changeFilterHandler({
    isNodeVisible: (statuses, node) => {
      return statuses.includes(node?.data.dto.meta_data?.operational_status);
    },
    onSetValues: setOperationalStatuses,
  });

  const handleSetOperatingModelStatus = changeFilterHandler<OperationalModel>({
    isNodeVisible: (statuses, node) => {
      return statuses.includes(node?.data.dto.meta_data?.operational_model);
    },
    onSetValues: setOperatingModelStatuses,
  });

  const handleSetPartners = changeFilterHandler<string>({
    isNodeVisible: (selectedPartners, node) => {
      return selectedPartners.some(
        (partner) => node?.data.dto.partners?.includes(partner),
      );
    },
    onSetValues: setPartners,
  });

  return (
    <Filters
      count={selectedFilters.length}
      onClearFilters={handleClearAllFilters}
    >
      <FilterSelector
        title="By validation status"
        values={validationStatus}
        onChange={handleSetStatus as (values: (string | number)[]) => void}
        options={validationStatusOptions}
        expandByDefault
      />

      <FilterSelector
        title="By review status"
        values={reviewStatus}
        onChange={handleSetReview as (values: (string | number)[]) => void}
        options={reviewStatusOptions}
        expandByDefault
      />

      <FilterSelector
        title="By operational status"
        values={operationalStatuses}
        onChange={
          handleSetOperationalStatus as (values: (string | number)[]) => void
        }
        options={OperationalStatusOptions}
        expandByDefault
      />

      <FilterSelector
        title="By operating model"
        values={operatingModelStatuses}
        onChange={
          handleSetOperatingModelStatus as (values: (string | number)[]) => void
        }
        options={OperationalModelOptions}
        expandByDefault
      />

      <PartnersFilterSelector
        partners={partners}
        onChange={handleSetPartners}
      />

      <CiaFilters />
    </Filters>
  );
};

export default ModelFilters;
